import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Box, Button, FormControlLabel, Switch } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  getProviders,
  getFDConfigs,
  getMPConfigs,
  getMPPointConfigs,
  getCloverPointConfigs,
  getIzipayConfigs,
  getMPCheckoutConfigs,
  getFiservQrConfigs,
  getSitefConfigs,
} from 'services/paymentsServices';
import SelectField from 'components/SelectField';
import ConfirmDialog from 'components/ConfirmDialog';
import VenuePaymentConfigForm from './schemas/VenuePaymentConfigForm';
import VenuePaymentConfigFormProps from 'types/forms/VenuePaymentConfigFormProps';
import PaymentProvider from 'types/models/PaymentProvider';
import FirstDataConfig from 'types/models/FirstDataConfig';
import MercadoPagoConfig from 'types/models/MercadoPagoConfig';
import { updateVenuePaymentConfig } from 'services/venuesServices';
import IzipayConfig from 'types/models/IzipayConfig';
import MercadoPagoPointConfig from 'types/models/MercadoPagoPointConfig';
import CloverPointConfig from 'types/models/CloverPointConfig';
import MercadoPagoCheckoutConfig from 'types/models/MercadoPagoCheckoutConfig';
import FiservQrSubsidiaryConfig from 'types/models/FiservQrSubsidiaryConfig';
import SitefConfig from 'types/models/SitefConfig';
import ImageInput from 'components/ImageInput';
import 'styles/forms/VenuePaymentConfigForm.scss';

export default ({ venue }: VenuePaymentConfigFormProps): JSX.Element => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [enableCustomQrScreenAspect, setEnableCustomQrScreenAspect] = React.useState<boolean>(
    venue.paymentConfig?.fiservQrAspect !== null,
  );
  const [providerList, setProviderList] = React.useState<PaymentProvider[]>([]);
  const [firstDataList, setFirstDataList] = React.useState<FirstDataConfig[]>([]);
  const [mpList, setMPList] = React.useState<MercadoPagoConfig[]>([]);
  const [mpCheckoutList, setMPCheckoutList] = React.useState<MercadoPagoCheckoutConfig[]>([]);
  const [MPPointList, setMPPointList] = React.useState<MercadoPagoPointConfig[]>([]);
  const [cloverPointList, setCloverPointList] = React.useState<CloverPointConfig[]>([]);
  const [izipayList, setIzipayList] = React.useState<IzipayConfig[]>([]);
  const [fiservQrSubsidiaryConfigList, setFiservQrSubsidiaryConfigList] = React.useState<
    FiservQrSubsidiaryConfig[] | []
  >([]);
  const [sitefConfigList, setSitefConfigList] = React.useState<SitefConfig[] | []>([]);
  const customLogoAspect = venue.paymentConfig?.fiservQrAspect?.fiservQrScreenLogo;
  const { t } = useTranslation();

  React.useEffect(() => {
    async function getData() {
      const getFDConfigsResult = await getFDConfigs();
      setFirstDataList(getFDConfigsResult);
      const getMPConfigsResult = await getMPConfigs();
      setMPList(getMPConfigsResult);
      const getMPPConfigsResult = await getMPPointConfigs();
      setMPPointList(getMPPConfigsResult);
      const getMPCheckoutConfigsResult = await getMPCheckoutConfigs();
      setMPCheckoutList(getMPCheckoutConfigsResult);
      const getCPConfigsResult = await getCloverPointConfigs();
      setCloverPointList(getCPConfigsResult);
      const getIPConfigsResult = await getIzipayConfigs();
      setIzipayList(getIPConfigsResult);
      const getFiservQrConfigsResult = await getFiservQrConfigs();
      if (getFiservQrConfigsResult) setFiservQrSubsidiaryConfigList(getFiservQrConfigsResult);
      const getSitefConfigsResult = await getSitefConfigs();
      if (getSitefConfigsResult) setSitefConfigList(getSitefConfigsResult);
      const getProvidersResult = await getProviders('venue');
      setProviderList(getProvidersResult);
    }

    getData();
  }, []);

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const onConfirm = async (): Promise<void> => {
    if (!venue) return;
    try {
      const body = {
        id: '0',
        priorityOverCommerce: formik.values.priorityOverCommerce,
        cashEnabled: formik.values.cashEnabled,
        balanceEnabled: formik.values.balanceEnabled,
        courtesyEnabled: formik.values.courtesyEnabled,
        splitPaymentEnabled: formik.values.splitPaymentEnabled,
        activeProvider: formik.values.activeProvider,
        firstDataConfigId: formik.values.firstDataConfigId ?? undefined,
        mpConfigId: formik.values.mpConfigId ?? undefined,
        mpCheckoutConfigId: formik.values.mpCheckoutConfigId ?? undefined,
        mpPointConfigId: formik.values.mpPointConfigId ?? undefined,
        cloverPointConfigId: formik.values.cloverPointConfigId ?? undefined,
        izipayConfigId: formik.values.izipayConfigId ?? undefined,
        fiservQrConfigId: formik.values.fiservQrConfigId ?? undefined,
        sitefConfigId: formik.values.sitefConfigId ?? undefined,
        fiservQrAspect: enableCustomQrScreenAspect
          ? {
              fiservQrScreenLetterColor: formik.values.fiservQrScreenLetterColor ?? undefined,
              fiservQrScreenQrBorderColor: formik.values.fiservQrScreenQrBorderColor ?? undefined,
              fiservQrScreenBorderColor: formik.values.fiservQrScreenBorderColor ?? undefined,
              fiservQrScreenBackgroundColor: formik.values.fiservQrScreenBackgroundColor ?? undefined,
              fiservQrScreenLogo: formik.values.fiservQrScreenLogo
                ? (formik.values.fiservQrScreenLogo as any)
                : undefined,
            }
          : null,
      };

      const data = await updateVenuePaymentConfig(venue.id, body);
      if (data) {
        history.go(-1);
      }
    } catch {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      priorityOverCommerce: venue?.paymentConfig?.priorityOverCommerce ?? false,
      cashEnabled: venue?.paymentConfig?.cashEnabled ?? false,
      balanceEnabled: venue?.paymentConfig?.balanceEnabled ?? false,
      courtesyEnabled: venue?.paymentConfig?.courtesyEnabled ?? false,
      splitPaymentEnabled: venue?.paymentConfig?.splitPaymentEnabled ?? false,
      activeProvider: venue?.paymentConfig?.activeProvider ?? undefined,
      firstDataConfigId: venue?.paymentConfig?.firstDataConfigId ?? undefined,
      mpConfigId: venue?.paymentConfig?.mpConfigId ?? undefined,
      mpCheckoutConfigId: venue?.paymentConfig?.mpCheckoutConfigId ?? undefined,
      mpPointConfigId: venue?.paymentConfig?.mpPointConfigId ?? undefined,
      cloverPointConfigId: venue?.paymentConfig?.cloverPointConfigId ?? undefined,
      izipayConfigId: venue?.paymentConfig?.izipayConfigId ?? undefined,
      fiservQrConfigId: venue?.paymentConfig?.fiservQrConfigId ?? undefined,
      sitefConfigId: venue?.paymentConfig?.sitefConfigId ?? undefined,
      enableCustomQrScreenAspect,
      fiservQrScreenLetterColor: venue.paymentConfig?.fiservQrAspect?.fiservQrScreenLetterColor ?? '#000000',
      fiservQrScreenQrBorderColor: venue.paymentConfig?.fiservQrAspect?.fiservQrScreenQrBorderColor ?? '#000000',
      fiservQrScreenBorderColor: venue.paymentConfig?.fiservQrAspect?.fiservQrScreenBorderColor ?? '#000000',
      fiservQrScreenBackgroundColor: venue.paymentConfig?.fiservQrAspect?.fiservQrScreenBackgroundColor ?? '#000000',
      fiservQrScreenLogo: customLogoAspect?.data ?? null,
    },
    validationSchema: VenuePaymentConfigForm(t),
    onSubmit: async () => {
      setOpenDialog(true);
    },
  });

  const providersOptions = providerList.map((provider: PaymentProvider) => ({
    value: provider.id,
    label: provider.description,
  }));

  const firstDataOptions = firstDataList.map((fdItem: FirstDataConfig) => ({
    value: fdItem.id,
    label: fdItem.description,
  }));

  const mpOptions = mpList.map((mpItem: MercadoPagoConfig) => ({
    value: mpItem.id,
    label: mpItem.description,
  }));

  const mpCheckoutOptions = mpCheckoutList.map((mpCheckoutItem: MercadoPagoCheckoutConfig) => ({
    value: mpCheckoutItem.id,
    label: mpCheckoutItem.description,
  }));

  const mpPointOptions = MPPointList.map((mpItem: MercadoPagoPointConfig) => ({
    value: mpItem.id,
    label: mpItem.description,
  }));

  const cloverPointOptions = cloverPointList.map((cloverPoint: CloverPointConfig) => ({
    value: cloverPoint.id,
    label: cloverPoint.description,
  }));

  const izipayOptions = izipayList.map((izipayItem: IzipayConfig) => ({
    value: izipayItem.id,
    label: izipayItem.description,
  }));

  const fiservQrOptions = fiservQrSubsidiaryConfigList.map(
    (fiservQrSubsidiaryConfigItem: FiservQrSubsidiaryConfig) => ({
      value: fiservQrSubsidiaryConfigItem.id,
      label: fiservQrSubsidiaryConfigItem.name,
    }),
  );

  const sitefOptions = sitefConfigList.map((sitefConfigItem: SitefConfig) => ({
    value: sitefConfigItem.id,
    label: sitefConfigItem.description,
  }));

  return (
    <>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <FormControlLabel
            id="priorityOverCommerce"
            name="priorityOverCommerce"
            control={<Switch checked={formik.values.priorityOverCommerce} onChange={formik.handleChange} />}
            label={t('venuePaymentConfig-form-priorityOverCommerce')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="cashEnabled"
            name="cashEnabled"
            control={<Switch checked={formik.values.cashEnabled} onChange={formik.handleChange} />}
            label={t('venuePaymentConfig-form-cashEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="balanceEnabled"
            name="balanceEnabled"
            control={<Switch checked={formik.values.balanceEnabled} onChange={formik.handleChange} />}
            label={t('venuePaymentConfig-form-balanceEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="courtesyEnabled"
            name="courtesyEnabled"
            control={<Switch checked={formik.values.courtesyEnabled} onChange={formik.handleChange} />}
            label={t('venuePaymentConfig-form-courtesyEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="splitPaymentEnabled"
            name="splitPaymentEnabled"
            control={<Switch checked={formik.values.splitPaymentEnabled} onChange={formik.handleChange} />}
            label={t('venuePaymentConfig-form-splitPaymentEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <SelectField
          name="activeProvider"
          label={t('venuePaymentConfig-form-activeProvider-label')}
          placeholder={t('venuePaymentConfig-form-activeProvider-placeholder')}
          value={formik.values.activeProvider}
          options={providersOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.activeProvider}
          error={formik.errors.activeProvider}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />
        <SelectField
          name="firstDataConfigId"
          label={t('venuePaymentConfig-form-firstDataConfigId-label')}
          placeholder={t('venuePaymentConfig-form-firstDataConfigId-placeholder')}
          value={formik.values.firstDataConfigId}
          options={firstDataOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.firstDataConfigId}
          error={formik.errors.firstDataConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="mpConfigId"
          label={t('venuePaymentConfig-form-mpConfigId-label')}
          placeholder={t('venuePaymentConfig-form-mpConfigId-placeholder')}
          value={formik.values.mpConfigId}
          options={mpOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.mpConfigId}
          error={formik.errors.mpConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="mpCheckoutConfigId"
          label={t('venuePaymentConfig-form-mpCheckoutConfigId-label')}
          placeholder={t('venuePaymentConfig-form-mpCheckoutConfigId-placeholder')}
          value={formik.values.mpCheckoutConfigId}
          options={mpCheckoutOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.mpCheckoutConfigId}
          error={formik.errors.mpCheckoutConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="mpPointConfigId"
          label={t('venuePaymentConfig-form-mpPointConfigId-label')}
          placeholder={t('venuePaymentConfig-form-mpPointConfigId-placeholder')}
          value={formik.values.mpPointConfigId}
          options={mpPointOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.mpPointConfigId}
          error={formik.errors.mpPointConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="cloverPointConfigId"
          label={t('venuePaymentConfig-form-cloverPointConfigId-label')}
          placeholder={t('venuePaymentConfig-form-cloverPointConfigId-placeholder')}
          value={formik.values.cloverPointConfigId}
          options={cloverPointOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.cloverPointConfigId}
          error={formik.errors.cloverPointConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="izipayConfigId"
          label={t('venuePaymentConfig-form-izipayConfigId-label')}
          placeholder={t('venuePaymentConfig-form-izipayConfigId-placeholder')}
          value={formik.values.izipayConfigId}
          options={izipayOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.izipayConfigId}
          error={formik.errors.izipayConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="fiservQrConfigId"
          label={t('commercePaymentConfig-form-fiservQrConfigId-label')}
          placeholder={t('commercePaymentConfig-form-fiservQrConfigId-placeholder')}
          value={formik.values.fiservQrConfigId}
          options={fiservQrOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.fiservQrConfigId}
          error={formik.errors.fiservQrConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
        <SelectField
          name="sitefConfigId"
          label={t('commercePaymentConfig-form-sitefConfigId-label')}
          placeholder={t('commercePaymentConfig-form-sitefConfigId-placeholder')}
          value={formik.values.sitefConfigId}
          options={sitefOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.sitefConfigId}
          error={formik.errors.sitefConfigId}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />

        <Box>
          <FormControlLabel
            id="enabledCustom"
            name="enabledCustom"
            control={
              <Switch
                checked={enableCustomQrScreenAspect}
                onChange={() => setEnableCustomQrScreenAspect(!enableCustomQrScreenAspect)}
              />
            }
            label={t('venuePaymentConfig-form-qr-aspect-enabled-custom')}
            disabled={formik.isSubmitting}
          />
        </Box>

        {/*fiservQrAspect*/}
        {enableCustomQrScreenAspect && (
          <div>
            <div className="fiservQrScreen-aspects-container">
              <div className="fiservQrScreen-colors">
                <div>
                  <input
                    id="fiservQrScreenLetterColor"
                    name="fiservQrScreenLetterColor"
                    type="color"
                    value={formik.values.fiservQrScreenLetterColor}
                    onChange={(event) => {
                      formik.setFieldValue('fiservQrScreenLetterColor', event.target.value);
                    }}
                  />
                  <label htmlFor="fiservQrScreenLetterColor">{t('venuePaymentConfig-form-qr-aspect-font-color')}</label>
                </div>
                <div>
                  <input
                    id="fiservQrScreenQrBorderColor"
                    name="fiservQrScreenQrBorderColor"
                    type="color"
                    value={formik.values.fiservQrScreenQrBorderColor}
                    onChange={(event) => {
                      formik.setFieldValue('fiservQrScreenQrBorderColor', event.target.value);
                    }}
                  />
                  <label htmlFor="fiservQrScreenQrBorderColor">
                    {t('venuePaymentConfig-form-qr-aspect-qr-border-color')}
                  </label>
                </div>
                <div>
                  <input
                    id="fiservQrScreenBorderColor"
                    name="fiservQrScreenBorderColor"
                    type="color"
                    value={formik.values.fiservQrScreenBorderColor}
                    onChange={(event) => {
                      formik.setFieldValue('fiservQrScreenBorderColor', event.target.value);
                    }}
                  />
                  <label htmlFor="fiservQrScreenBorderColor">
                    {t('venuePaymentConfig-form-qr-aspect-screen-border-color')}
                  </label>
                </div>
                <div>
                  <input
                    id="fiservQrScreenBackgroundColor"
                    name="fiservQrScreenBackgroundColor"
                    type="color"
                    value={formik.values.fiservQrScreenBackgroundColor}
                    onChange={(event) => {
                      formik.setFieldValue('fiservQrScreenBackgroundColor', event.target.value);
                    }}
                  />
                  <label htmlFor="fiservQrScreenBackgroundColor">
                    {t('venuePaymentConfig-form-qr-aspect-background-color')}
                  </label>
                </div>
              </div>
              <div className="fiservQrScreen-image">
                <ImageInput
                  label={t('venuePaymentConfig-form-qr-aspect-custom-logo')}
                  name="fiservQrScreenLogo"
                  isSubmitting={formik.isSubmitting}
                  value={formik.values.fiservQrScreenLogo}
                  setFieldValue={formik.setFieldValue}
                  touched={formik.touched.fiservQrScreenLogo}
                  errors={formik.errors.fiservQrScreenLogo}
                  previewDimensions={{ width: 150, height: 100 }}
                  uploadButtonDimensions={{ width: 180, height: 40 }}
                />
                <div className="user-message">{t('venuePaymentConfig-form-qr-aspect-custom-logo-ideal-sizes')}</div>
              </div>
            </div>
          </div>
        )}
        <Box marginTop="1rem">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {t('venuePaymentConfig-form-saveConfig')}
          </Button>
        </Box>
      </form>
    </>
  );
};
